import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader, Message } from 'rsuite';

import StaffingScenarios from '../components/StaffingScenarios';
import { useProjects } from '../hooks/useQueries';
import useStaffingOutlookModel from '../hooks/useStaffingOutlookModel';
import './TaskOrderDetailPage.css';
import './StaffingOutlookPage.css';

/*
 * Staffing scenarios for a task order, on their own page under the staffing outlook.
 * The outlook's numbers (spend to date, budget, where each month is headed) come from
 * the same hook the outlook page uses, so a scenario is always judged against the
 * figures the PM just looked at.
 */
export default function StaffingScenariosPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);

  const { data: projects } = useProjects();
  const project = useMemo(
    () => (Array.isArray(projects) ? projects : projects?.projects || []).find((p) => Number(p?.id) === projectId) || null,
    [projectId, projects]
  );

  const { data, isLoading, error, model, currentMonth, months, endMonth, budget } = useStaffingOutlookModel(projectId, taskOrderId);

  const outlookDollarsByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) out[ms] = model.monthTotals[ms]?.dollars ?? 0;
    return out;
  }, [model.monthTotals, months]);

  const odcDollarsByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) out[ms] = model.odcCells[ms]?.dollars ?? 0;
    return out;
  }, [model.odcCells, months]);

  const taskOrderPath = `/projects/${projectId}/task-orders/${taskOrderId}`;
  const outlookPath = `${taskOrderPath}/staffing-outlook`;

  return (
    <div className="ppc-page ppc-tod ppc-so">
      <header className="ppc-tod__header">
        <div style={{ minWidth: 0 }}>
          <div className="ppc-tod__crumbs">
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate('/')}>Projects</button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate(`/projects/${projectId}`)}>
              {project?.name || 'Project'}
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate(taskOrderPath)}>
              {data?.taskOrderName || 'Task order'}
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate(outlookPath)}>
              Staffing Outlook
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <span className="ppc-tod__crumb-current">Scenarios</span>
          </div>
          <h1 className="ppc-tod__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-sliders" aria-hidden="true" />
              Staffing Scenarios
            </span>
          </h1>
        </div>

        <div className="ppc-tod__actions">
          <button type="button" className="ppc-tod-btn" onClick={() => navigate(outlookPath)}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to outlook
          </button>
        </div>
      </header>

      {error ? (
        <Message type="error" showIcon>Could not load the staffing outlook: {String(error?.message || error)}</Message>
      ) : isLoading ? (
        <div className="ppc-so__loading"><Loader content="Loading…" /></div>
      ) : (
        <StaffingScenarios
          projectId={projectId}
          taskOrderId={taskOrderId}
          currentMonth={currentMonth}
          taskOrderEndMonth={endMonth}
          budget={budget}
          spentToDate={model.spentToDate}
          outlookMonths={months}
          outlookDollarsByMonth={outlookDollarsByMonth}
          odcDollarsByMonth={odcDollarsByMonth}
        />
      )}
    </div>
  );
}
